import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { trackLead } from '../lib/tracking';

const interestOptions = [
  'Prywatny system AIOs',
  'Własne rozwiązanie',
  'Generacja Klientów',
  'Strona internetowa',
  'Automatyzacja procesów',
];

interface InlineInputProps {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder: string;
}

function InlineInput({ value, onChange, type = 'text', placeholder }: InlineInputProps) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-transparent border-b border-stroke focus:border-accent-from outline-none px-2 pb-1 mx-1 inline-block text-text-primary placeholder:text-muted/60 transition-colors text-center"
      style={{ width: `${Math.max(placeholder.length, 14)}ch` }}
    />
  );
}

type Status = 'idle' | 'submitting' | 'sent' | 'error';
const emptyFields = { name: '', company: '', role: '', phone: '', email: '', message: '' };

export default function ContactFormBooking() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fields, setFields] = useState({ ...emptyFields });
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');

  const set = (key: keyof typeof fields) => (v: string) => setFields((prev) => ({ ...prev, [key]: v }));

  const togglePill = (option: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(option)) next.delete(option);
      else next.add(option);
      return next;
    });
  };

  const emailOk = /\S+@\S+\.\S+/.test(fields.email.trim());
  const isComplete =
    fields.name.trim() !== '' &&
    fields.company.trim() !== '' &&
    fields.role.trim() !== '' &&
    fields.phone.trim() !== '' &&
    emailOk &&
    selected.size >= 1;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isComplete) {
      setError('Uzupełnij wszystkie pola i zaznacz min. jedno zainteresowanie.');
      return;
    }
    setStatus('submitting');
    setError('');
    try {
      const body = new URLSearchParams({
        'form-name': 'contact',
        name: fields.name.trim(),
        company: fields.company.trim(),
        role: fields.role.trim(),
        interest: Array.from(selected).join(', '),
        email: fields.email.trim(),
        phone: fields.phone.trim(),
        message: fields.message.trim(),
      });
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error('bad status');
      trackLead('contact');
      setStatus('sent');
    } catch {
      setStatus('error');
      setError('Nie udało się wysłać. Spróbuj ponownie lub napisz na dawid@daien.pl.');
    }
  }

  return (
    <section id="kontakt" aria-label="Formularz kontaktowy" className="py-20 md:py-32 bg-bg">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 text-center" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="mb-16"
        >
          <div className="inline-flex items-center justify-center gap-4">
            <span className="w-8 h-px bg-stroke"></span>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Kontakt</span>
            <span className="w-8 h-px bg-stroke"></span>
          </div>
        </motion.div>

        {status === 'sent' ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto py-16"
          >
            <p className="text-3xl md:text-4xl font-light text-text-primary mb-4">Dziękuję, wiadomość poszła.</p>
            <p className="text-lg text-muted">Odezwę się najszybciej jak się da, zwykle tego samego dnia.</p>
          </motion.div>
        ) : (
          <motion.form
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          >
            <input type="hidden" name="form-name" value="contact" />
            <p hidden>
              <label>
                Don't fill this out: <input name="bot-field" />
              </label>
            </p>

            <div className="text-2xl md:text-3xl font-light leading-[2] text-text-primary">
              <span className="text-muted">Cześć Dawid, nazywam się</span>
              <InlineInput value={fields.name} onChange={set('name')} placeholder="imię i nazwisko" />
              <span className="text-muted">z</span>
              <InlineInput value={fields.company} onChange={set('company')} placeholder="firma" />
              <span className="text-muted">.</span>
            </div>

            <div className="text-2xl md:text-3xl font-light leading-[2] text-text-primary mt-4">
              <span className="text-muted">Moje stanowisko to</span>
              <InlineInput value={fields.role} onChange={set('role')} placeholder="np. CEO, marketing manager" />
              <span className="text-muted">.</span>
            </div>

            <div className="text-2xl md:text-3xl font-light leading-[2] text-text-primary mt-4">
              <span className="text-muted">Zadzwoń do mnie pod</span>
              <InlineInput value={fields.phone} onChange={set('phone')} type="tel" placeholder="+48 600 000 000" />
              <span className="text-muted">.</span>
            </div>

            <div className="mt-16 mb-12">
              <p className="text-2xl md:text-3xl font-light text-muted mb-6">Chciałbym zastosować u siebie:</p>
              <div className="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto">
                {interestOptions.map((option) => {
                  const isActive = selected.has(option);
                  return (
                    <button
                      type="button"
                      key={option}
                      onClick={() => togglePill(option)}
                      className={`text-base md:text-lg px-5 py-2.5 rounded-full border transition-all ${
                        isActive
                          ? 'bg-text-primary text-bg border-text-primary'
                          : 'bg-surface text-text-primary border-stroke hover:border-text-primary'
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="text-2xl md:text-3xl font-light leading-[2] text-text-primary mt-16 mb-16">
              <span className="text-muted">Odpisz mi na mój email</span>
              <InlineInput value={fields.email} onChange={set('email')} type="email" placeholder="email@firma.pl" />
              <span className="text-muted">.</span>
            </div>

            <div className="mt-12 text-left max-w-3xl mx-auto">
              <label htmlFor="message" className="text-2xl md:text-3xl font-light text-muted block mb-3 text-center">
                P.S.
              </label>
              <textarea
                id="message"
                name="message"
                value={fields.message}
                onChange={(e) => set('message')(e.target.value)}
                rows={4}
                placeholder="Coś więcej? Kontekst, link do strony, konkretne pytanie..."
                className="w-full bg-surface border border-stroke rounded-2xl px-6 py-4 text-base text-text-primary placeholder:text-muted/70 focus:border-accent-from outline-none transition-colors resize-y"
              />
            </div>

            <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-6">
              <button
                type="submit"
                disabled={!isComplete || status === 'submitting'}
                className={`inline-flex items-center gap-2 px-10 py-4 rounded-full text-base font-medium transition-transform ${
                  !isComplete || status === 'submitting'
                    ? 'bg-text-primary/40 text-bg/70 cursor-not-allowed'
                    : 'bg-text-primary text-bg hover:scale-[1.02]'
                }`}
              >
                {status === 'submitting' ? 'Wysyłam...' : 'Wyślij wiadomość ->'}
              </button>
              <span className="text-sm text-muted">
                lub{' '}
                <a
                  href="https://calendar.app.google/4V6jKtrVUtMihmoE9"
                  target="_blank"
                  rel="noreferrer"
                  className="text-text-primary border-b border-stroke hover:border-text-primary transition-colors"
                >
                  umów 20-min rozmowę
                </a>
              </span>
            </div>

            {error && <p className="mt-6 text-sm text-accent-from">{error}</p>}
            {!isComplete && status !== 'submitting' && (
              <p className="mt-4 text-xs text-muted/70">Wypełnij wszystkie pola i zaznacz min. jedno zainteresowanie, aby wysłać.</p>
            )}
          </motion.form>
        )}
      </div>
    </section>
  );
}
